import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login, { AdminUser } from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Messages from "./pages/Messages";
import Staff from "./pages/Staff";
import Publications from "./pages/Publications";
import Venues from "./pages/Venues";
import Payments from "./pages/Payments";
import Emails from "./pages/Emails";
import Flash from "./pages/Flash";
import Vibes from "./pages/Vibes";
import Layout from "./components/Layout";
import { setOnSessionExpired } from "./utils/api";

// Même barème que côté nav (components/Layout.tsx) et backend (requireStaffRole,
// middleware/auth.ts) — évite qu'un modérateur atteigne une page admin-only en tapant l'URL
// directement (la nav la masque déjà, ceci bloque aussi l'accès direct).
const STAFF_LEVELS: Record<string, number> = { moderator: 1, admin: 2 };
const staffLevel = (u: AdminUser | null) => (u?.is_master_admin ? 99 : u?.staff_role ? STAFF_LEVELS[u.staff_role] || 0 : 0);

const storedUser = localStorage.getItem("admin_user");

export default function App() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [user, setUser] = useState<AdminUser | null>(storedUser ? JSON.parse(storedUser) : null);

  const handleLogin = (t: string, u: AdminUser) => {
    localStorage.setItem("admin_token", t);
    localStorage.setItem("admin_user", JSON.stringify(u));
    setToken(t);
    setUser(u);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_user");
    setToken(null);
    setUser(null);
  };

  // Rafraîchissement du jeton en échec (refresh token invalide/expiré, voir utils/api.ts) —
  // jusqu'ici rien ne redirigeait proprement vers la connexion, d'où l'écran qui semblait juste
  // vide après expiration plutôt que de redemander une connexion.
  useEffect(() => {
    setOnSessionExpired(() => handleLogout());
    return () => setOnSessionExpired(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) return <Login onLogin={handleLogin} />;

  const level = staffLevel(user);

  return (
    <BrowserRouter>
      <Layout onLogout={handleLogout} user={user}>
        <Routes>
          {level >= 2 && <Route path="/" element={<Dashboard />} />}
          <Route path="/users" element={<Users />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/venues" element={<Venues />} />
          <Route path="/flash" element={<Flash />} />
          <Route path="/vibes" element={<Vibes />} />
          <Route path="/reports" element={<Reports />} />
          {level >= 99 && <Route path="/messages" element={<Messages />} />}
          {level >= 99 && <Route path="/staff" element={<Staff />} />}
          {level >= 2 && <Route path="/payments" element={<Payments />} />}
          {level >= 2 && <Route path="/emails" element={<Emails />} />}
          <Route path="*" element={<Navigate to={level >= 2 ? "/" : "/users"} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}
